import { Box, Typography, Grid, CircularProgress, Divider, Card } from '@mui/material';
import { Formik, Form } from 'formik';
import axios from 'axios';
import React, { useState, useEffect } from "react";
import { READ_DUTY } from '../../../services/apiRoutes';
import CustomCardView from '../../common/CustomCardView';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from '../../common/DetailsPage/DetailsParticulars';
import { convertNumberToWords, printFunction } from '../../../services/apiServices';
import CustomPrintModel from '../../common/CustomPrint';
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from '../../../constants/translationHelper';


const DutyView = ({ handleClose, dutyId, currencySym }) => {
    const companyDateFormat = useCompanyDateFormat();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataParticular, setDataParticular] = useState(null);
    const [totalQty, setTotalQty] = useState(0); // State for total quantity
    const [amountData, setAmount] = useState(0);
    const [grandTotalDuty, setGrandTotalDuty] = useState(0);
    const [grandTotalTax, setGrandTotalTax] = useState(0);
    const [totalDuty, setTotalDuty] = useState(0);
    const [totalTax, setTotalTax] = useState(0);
    const [amountText, setAmountText] = useState(0);
    const [currencyName, setCurrencyName] = useState('');


    const number = amountText || '';
    const amountInWords = convertNumberToWords(number);

    useEffect(() => {
        if (!dutyId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_DUTY(dutyId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (response.data.success) {
                    const dutyData = response.data.duty;
                    const symbol = dutyData?.currency?.currency_symbol || '';
                    const symbolName = dutyData?.currency?.currency_name || '';

                    const boeConversion = parseFloat(dutyData.boeConversionRate || 0);

                    // Map particulars array
                    const particulars = dutyData.particulars.map(particular => ({
                        particularId: particular.dutyParticularId,
                        productName: particular?.products?.productName,
                        hsn: particular.hsn,
                        duty: `${particular?.currencyDutyList?.currency_symbol || ''} ${parseFloat(particular?.duty || 0).toFixed(2)}`,
                        amount: `${symbol || ''} ${parseFloat(particular?.amount || 0).toFixed(2)}`,
                        tax: `${particular?.currencyTaxList?.currency_symbol || ''} ${parseFloat(particular?.tax || 0).toFixed(2)}`,
                        dutySymbol: particular.currencyDutyList?.currency_symbol || '',
                        taxSymbol: particular.currencyTaxList?.currency_symbol || '',
                        // keep raw for totals
                        amountRaw: particular?.amount || '',
                        dutyRaw: particular?.duty || '',
                        taxRaw: particular?.tax || '',
                    }));
                    const dutySymbol = particulars[0]?.dutySymbol || '';
                    const taxSymbol = particulars[0]?.taxSymbol || '';

                    // Calculate totals
                    const totalAmount = particulars.reduce((sum, item) => sum + parseFloat(item.amountRaw), 0).toFixed(2);
                    const totalDutyCalc = particulars.reduce((sum, item) => sum + parseFloat(item.dutyRaw), 0).toFixed(2);
                    const totalTaxCalc = particulars.reduce((sum, item) => sum + parseFloat(item.taxRaw), 0).toFixed(2);

                    // Format totals with currency symbol
                    const formattedTotalAmount = `${symbol} ${totalAmount}`;
                    const formattedTotalDuty = `${dutySymbol} ${totalDutyCalc}`;
                    const formattedTotalTax = `${taxSymbol} ${totalTaxCalc}`;

                    const grandTotalDuty = `${currencySym} ${(boeConversion * totalDutyCalc).toFixed(2)}`;
                    const grandTotaTax = `${currencySym} ${(boeConversion * totalTaxCalc).toFixed(2)}`;

                    setGrandTotalTax(grandTotaTax);
                    setGrandTotalDuty(grandTotalDuty);
                    setAmountText(totalAmount);
                    setData(dutyData);
                    setDataParticular(particulars);
                    setAmount(formattedTotalAmount);
                    setTotalQty(particulars.length);
                    setTotalDuty(formattedTotalDuty);
                    setTotalTax(formattedTotalTax);
                    setCurrencyName(symbolName);
                }

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dutyId]);



    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const getLastSuffix = (url) => {
        if (url) {
            const parts = url.split('/');
            return parts[parts.length - 1];
        }
        return '';
    };
    const values = {
        supplierNames: data?.clientCompany?.clientCompanyName || '',
        reference: data?.reference || '',
        commercialInvoiceNo: data?.commercialInvoiceNo || '',
        date: companyDateFormat(data?.date) || '',
        dueDate: companyDateFormat(data?.dueDate) || '',
        note: data?.note || '',
        paymentRefenceDuty: data?.paymentRefenceDuty || '',
        // ciUpload: data?.ciUpload || '',
        boeUpload: getLastSuffix(data?.boeUpload || ''),
        boeUploadUrl: data?.boeUpload || '',
        currency: data?.currency?.currency || '',
        voucherNo: data?.voucherNo || '',
        boeConversionRate: data?.boeConversionRate
            ? `${currencySym} ${parseFloat(data.boeConversionRate).toFixed(2)}`
            : '',
        boeNumber: data?.boeNumber || '',
        proformaInvoiceNo: data?.ProformaInvoice?.proformaInvoiceNo || '',
        particulars: data?.particulars.map(particular => ({
            particularName: parseInt(particular.particularName, 10),
            qty: particular.qty,
            rate: particular.rate,
            unit: parseInt(particular.unit, 10),
            description: particular.description,
        })) || [],
    };

    // Define labels for the displayed fields
    const labels = [
        { key: 'supplierNames', label: "Client Company", sm: 12, md: 4, },
        { key: 'date', label: "Date", sm: 12, md: 4, },
        { key: 'voucherNo', label: "Voucher No.", sm: 12, md: 4, },
        { key: 'currency', label: "currency", sm: 12, md: 4, },
        { key: 'boeConversionRate', label: "BOE Conversion Rate", sm: 12, md: 4, },
        { key: 'boeNumber', label: "BOE Number", sm: 12, md: 4, },
    ];
    const particularsColumns = [
        {
            id: 'productName',
            label: 'Product Name',
            accessor: 'productName',
        },
        {
            id: 'hsn',
            label: 'HSN No.',
            accessor: 'hsn',
            styleAccessor: {
                textAlign: 'center',
            },
        },
        {
            id: 'amount',
            label: 'Amount',
            accessor: 'amount',
            styleAccessor: {
                textAlign: 'right',
            },
        },

        {
            id: 'duty',
            label: 'Duty',
            accessor: 'duty',
            styleAccessor: {
                textAlign: 'right',
            },
        },
        {
            id: 'tax',
            label: 'Tax',
            accessor: 'tax',
            styleAccessor: {
                textAlign: 'right',
            },
        },
    ];


    const footerData = [
        {
            label: 'Total',
            footerValue: '',
            style: {
                textAlign: 'right',
            },
            colSpan: 2,
        },
        {
            label: '',
            footerValue: amountData,
            style: {
                textAlign: 'right',
            },
        },

        {
            label: '',
            footerValue: totalDuty,
            style: {
                textAlign: 'right',
            },
            colSpan: 1,
        },
        {
            label: '',
            footerValue: totalTax,
            style: {
                textAlign: 'right',

            },
            colSpan: 1,
        },
    ];
    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId);
    };
    return (
        <>
            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik
                    initialValues={values}
                    enableReinitialize
                >
                    {() => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                        {t("Duty")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose}>
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={0} sx={{ px: "0%" }}>
                                <Grid item xs={12} md={12} >
                                    <CustomCardView
                                        title="Bill To"
                                        label={labels}
                                        data={values}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Particulars")}
                                        </Typography>
                                    </Box>
                                    <DetailsParticulars
                                        title={'Particulars'}
                                        columns={particularsColumns}
                                        data={dataParticular}
                                        footerData={footerData}
                                    />
                                </Grid>

                                <Grid item xs={7}>
                                    <br />
                                    <Box>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("In Words")}
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "uppercase" }}>
                                            {currencyName} {amountInWords}
                                        </Typography>
                                    </Box>
                                    <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("payment Refence Duty")}  :
                                        </Typography>
                                        {values.paymentRefenceDuty}
                                    </Box>
                                    <Box style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("BOE Document")} :
                                        </Typography>
                                        <a href={values.boeUploadUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#007bff", textDecoration: "none" }}>
                                            {values.boeUpload}
                                        </a>
                                    </Box>
                                    <Box sx={{ pt: 5 }}>
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px" }}>
                                            {t("Note")} :
                                        </Typography>
                                        <Typography style={{ color: "#212529", fontWeight: 400, fontSize: "14px", textTransform: "capitalize" }}>
                                            {values.note}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={5}>
                                    <br />
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Summary")}
                                        </Typography>
                                    </Box>
                                    <Card sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 400 }} variant="outlined">
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{t("Invoice Amount")}:</Typography>
                                            <Typography> {amountData}</Typography>
                                        </Box>

                                        <Divider sx={{ my: 0 }} />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Invoice Amount")}:</Typography>
                                            <Typography fontWeight="bold">{amountData}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Grand Total Duty")}:</Typography>
                                            <Typography fontWeight="bold">{grandTotalDuty}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography fontWeight="bold">{t("Grand Total Tax")}:</Typography>
                                            <Typography fontWeight="bold">{grandTotalTax}</Typography>
                                        </Box>

                                    </Card>
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>

            <CustomPrintModel contentId="contentId">
                <Typography style={{ textAlign: "center" }}>  Invoice</Typography>

            </CustomPrintModel >
        </>
    );
};

export default DutyView;
